import { rosterComputedCell, rosterClassIcons, rosterThumbnailHtml } from './creature-cells';
import { parseCampaignRowsResponse } from '../shared/campaign-list-rows';
import type { CatalogListConfig } from './types';
import { campaignRosterView, type CampaignRosterView as CatalogMyCampaignPlayersResponse, type CampaignRosterPlayer as MyCampaignPlayerRow } from './campaign-roster-rows';
import { initTooltips, mountCatalogList, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';

declare const copyInputToClipboard: ((inputId: string) => void) | undefined;
declare const hideEl: ((id: string) => void) | undefined;
declare const showEl: ((id: string) => void) | undefined;

function icon(id: string, w = 20, h = 20, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function updateInviteSidelog(inviteCode: string): void {
    if (typeof openSideLog === 'function') openSideLog();
    const target = document.getElementById('div_sidelog_warning');
    if (!target) return;

    target.innerHTML = '';
    const msg = document.createElement('h5');
    msg.className = 'bg-success text-dark rounded border border-2 border-danger p-3 my-2';
    msg.textContent = `Campaign Invite Code Copied to Clipboard: ${inviteCode}`;
    target.appendChild(msg);
}

function wireInviteCopy(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-ft-campaign-invite-copy]:not([data-ft-mounted="1"])').forEach((button) => {
        button.dataset.ftMounted = '1';
        button.addEventListener('click', () => {
            const inputId = button.dataset.ftInputId || '';
            if (inputId && typeof copyInputToClipboard === 'function') copyInputToClipboard(inputId);
            updateInviteSidelog(button.dataset.ftInviteCode || '');
        });
    });
}

// BESIDE-GAME link mode: the table is a tool inside FT3D's Workspace (renderCampaignPlayersTool), so no link may navigate the
// play tab away. Every link opens in a new tab, and none of them targets a game page — a character goes to its map-free sheet.
function openBesideGame(a: HTMLAnchorElement): void {
    a.target = '_blank';
    a.rel = 'noopener';
}

// The invite-code copy pair the standalone page renders in PHP; the Workspace tool adds it once the endpoint has served the code.
function appendInviteCopy(parent: HTMLElement, campaignId: string, inviteCode: string): void {
    const inputId = `input_hidden_campaign_invite_code_tool_${campaignId}`;
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.id = inputId;
    hidden.value = inviteCode;
    const copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'btn btn-sm btn-success d-flex align-items-center flex-shrink-0';
    copy.setAttribute('aria-label', 'Copy Campaign Invite Code');
    copy.dataset.ftCampaignInviteCopy = '';
    copy.dataset.ftInputId = inputId;
    copy.dataset.ftInviteCode = inviteCode;
    copy.innerHTML = icon('copy_campaign_invite_code', 20, 20, 'Copy Campaign Invite Code');
    parent.replaceChildren(hidden, copy);
    wireInviteCopy();
}

function appendIconButton(
    parent: HTMLElement,
    href: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    besideGame: boolean,
): HTMLElement {
    const a = document.createElement('a');
    a.href = href;
    if (besideGame) openBesideGame(a);
    a.className = className;
    a.setAttribute('aria-label', ariaLabel);
    a.innerHTML = icon(iconId, 20, 20, ariaLabel);
    parent.appendChild(a);
    return a;
}

function appendActionButton(
    parent: HTMLElement,
    id: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    display: string,
    onClick: () => void | Promise<void>,
): HTMLButtonElement {
    const button = document.createElement('button');
    button.id = id;
    button.type = 'button';
    button.className = className;
    button.style.display = display;
    button.setAttribute('aria-label', ariaLabel);
    button.innerHTML = icon(iconId, 20, 20, ariaLabel);
    button.addEventListener('click', () => { void onClick(); });
    parent.appendChild(button);
    return button;
}

function appendCell(row: HTMLTableRowElement, className = ''): HTMLTableCellElement {
    const td = document.createElement('td');
    if (className) td.className = className;
    row.appendChild(td);
    return td;
}

function renderPlayerRow(player: MyCampaignPlayerRow, besideGame: boolean): HTMLTableRowElement {
    const row = document.createElement('tr');
    row.dataset.ftPlayerId = player.item_id;
    row.dataset.ftPlayerText = player.text || `${player.character_name} ${player.player_username}`.toLowerCase();

    appendCell(row, 'text-center align-middle').textContent = String(player.index);

    const picture = appendCell(row, 'text-center align-middle');
    const pictureWrap = document.createElement('div');
    pictureWrap.className = 'p-1';
    pictureWrap.innerHTML = rosterThumbnailHtml(player.thumbnail_inputs, true);
    picture.appendChild(pictureWrap);

    const character = appendCell(row);
    const characterLink = document.createElement('a');
    characterLink.className = 'link-primary';
    characterLink.href = player.character_url;
    characterLink.textContent = player.character_name || '';
    if (besideGame) {
        // item.php routes an attached PC into the FT3D play shell, which would idle this tab; view=sheet is its map-free sheet.
        characterLink.href = `${player.character_url}${player.character_url.includes('?') ? '&' : '?'}view=sheet`;
        openBesideGame(characterLink);
        // A character in the current encounter opens its FT3D Sheet window instead (my_campaign.php ftOpenFt3dEncounterSheet).
        characterLink.addEventListener('click', (event) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            const open = (window as unknown as { ftOpenFt3dEncounterSheet?: (id: string) => boolean }).ftOpenFt3dEncounterSheet;
            if (typeof open === 'function' && open(player.item_id)) event.preventDefault();
        });
    }
    character.appendChild(characterLink);

    const playerCell = appendCell(row);
    if (player.player_url) {
        const playerLink = document.createElement('a');
        playerLink.className = 'link-primary';
        playerLink.href = player.player_url;
        if (besideGame) openBesideGame(playerLink);
        playerLink.textContent = player.player_username || '';
        playerCell.appendChild(playerLink);
    } else {
        playerCell.textContent = player.player_username || '';
    }

    const level = appendCell(row, 'text-center');
    const badge = document.createElement('span');
    badge.className = 'h5 badge bg-dark border';
    badge.textContent = rosterComputedCell(player.client_computed, 'total_level');
    badge.dataset.ftCreatureStat = 'total_level';
    badge.dataset.ftCasterId = player.item_id;
    badge.dataset.ftStatComputed = player.client_computed == null ? '0' : '1';
    level.appendChild(badge);

    const classes = appendCell(row, 'text-center');
    classes.innerHTML = rosterClassIcons(player.client_creature ?? {});

    const actions = appendCell(row, 'text-center');
    const group = document.createElement('div');
    group.className = 'd-flex flex-wrap justify-content-center';
    actions.appendChild(group);

    appendIconButton(group, player.edit_url, 'btn btn-sm btn-info m-1', 'edit_player_character', 'Edit character', besideGame);

    const activeButton = appendActionButton(
        group,
        `btn_toggle_character_active_${player.item_id}`,
        'btn btn-sm btn-primary m-1',
        'player_character_active',
        'Character is ACTIVE — click to set inactive',
        player.inactive ? 'none' : '',
        async () => {
            // 2026-09-02: five players clicked this mid-lockup looking for "make me active" and
            // were marked inactive (camp, out of the party). The inactive direction now confirms.
            if (!window.confirm('Set this character INACTIVE? An inactive character leaves the active party and is skipped by session and party moves. (To rejoin later, use "Mark character active".)')) return;
            if (typeof openSideLog === 'function') openSideLog();
            await queryData(resolveUrl('options/tools/player_characters/update/update_player_character_inactive_inc.php'), 'div_sidelog_warning', {
                item_id: player.item_id,
                player_character_inactive: 1,
                csrf_token: player.csrf_token,
            });
            activeButton.style.display = 'none';
            inactiveButton.style.display = '';
        },
    );

    const inactiveButton = appendActionButton(
        group,
        `btn_toggle_character_inactive_${player.item_id}`,
        'btn btn-sm btn-danger m-1',
        'player_character_inactive',
        'Mark character active',
        player.inactive ? '' : 'none',
        async () => {
            if (typeof openSideLog === 'function') openSideLog();
            await queryData(resolveUrl('options/tools/player_characters/update/update_player_character_inactive_inc.php'), 'div_sidelog_warning', {
                item_id: player.item_id,
                player_character_inactive: 0,
                csrf_token: player.csrf_token,
            });
            activeButton.style.display = '';
            inactiveButton.style.display = 'none';
        },
    );

    const kickButton = appendActionButton(
        group,
        `btn_toggle_character_kick_${player.item_id}`,
        'btn btn-sm btn-warning m-1',
        'kick_player_character_from_campaign',
        'Kick character from campaign',
        '',
        () => {
            if (typeof hideEl === 'function') hideEl(kickButton.id);
            else kickButton.style.display = 'none';
            if (typeof showEl === 'function') showEl(confirmKick.id);
            else confirmKick.style.display = '';
        },
    );

    const confirmKick = appendActionButton(
        group,
        `btn_toggle_character_kick_check_${player.item_id}`,
        'btn btn-sm btn-danger m-1',
        'confirm_kick_player_character_from_campaign',
        'Confirm kick character',
        'none',
        async () => {
            await queryData(resolveUrl('options/tools/campaigns/update/update_kick_player_character_from_campaign_inc.php'), 'div_sidelog_warning', {
                item_id: player.item_id,
                csrf_token: player.csrf_token,
            });
            if (typeof openSideLog === 'function') openSideLog();
            row.remove();
        },
    );

    return row;
}

function buildTable(container: HTMLElement, data: CatalogMyCampaignPlayersResponse, searchInput: HTMLInputElement | null, emptyText: string, besideGame: boolean): void {
    container.innerHTML = '';

    const responsive = document.createElement('div');
    responsive.className = 'table-responsive-xxl';
    container.appendChild(responsive);

    const table = document.createElement('table');
    table.className = 'ft-sortable';
    responsive.appendChild(table);

    const thead = document.createElement('thead');
    thead.innerHTML = `
        <tr>
            <th class="text-center ft-no-filter" style="width: 50px;">#</th>
            <th class="text-center ft-no-sort ft-no-filter" style="width: 100px;">${icon('picture')}</th>
            <th class="text-left">Character Name</th>
            <th class="text-left">Player</th>
            <th class="text-center" style="width: 80px;">${icon('level')}</th>
            <th class="text-center" style="width: 160px;">${icon('class')}</th>
            <th class="text-center ft-no-sort ft-no-filter">${icon('settings')}</th>
        </tr>`;
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    table.appendChild(tbody);

    const rows = (data.players || []).map((player) => {
        const row = renderPlayerRow(player, besideGame);
        tbody.appendChild(row);
        return row;
    });

    const emptyEl = document.createElement('div');
    emptyEl.className = 'text-center text-muted py-5';
    emptyEl.textContent = emptyText;
    emptyEl.style.display = rows.length ? 'none' : '';
    container.appendChild(emptyEl);

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        let visible = 0;
        for (const row of rows) {
            if (!row.isConnected) continue;
            const show = !keyword || (row.dataset.ftPlayerText || '').includes(keyword);
            row.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        responsive.style.display = visible === 0 ? 'none' : '';
        emptyEl.textContent = keyword ? 'No players match your search.' : emptyText;
        emptyEl.style.display = visible === 0 ? '' : 'none';
    };

    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    (window as unknown as { ftMountPcSheet?: (root: ParentNode) => void }).ftMountPcSheet?.(container);
    (window as unknown as { ftMountThumbnails?: (root: Element) => void }).ftMountThumbnails?.(container);
    initTooltips(container);
    if (typeof window.ftSortableScan === 'function') window.ftSortableScan();
    applyFilter();
}

export function mountCatalogMyCampaignPlayers(root: HTMLElement, config: CatalogListConfig): void {
    wireInviteCopy();

    const container = results(root);
    if (!container) {
        console.error('[catalog-my-campaign-players] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No campaign players found.';
    const besideGame = root.dataset.ftLinkMode === 'beside-game';

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            const raw = await res.text();
            const data = campaignRosterView(parseCampaignRowsResponse(raw, res.status, 'campaign-roster-v1'));
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            buildTable(container, data, searchInput, emptyText, besideGame);
            const inviteSlot = root.closest('[data-ft-campaign-players-tool]')?.querySelector<HTMLElement>('[data-ft-campaign-invite-slot]');
            if (inviteSlot && data.invite_code) appendInviteCopy(inviteSlot, inviteSlot.dataset.ftCampaignInviteSlot || '', data.invite_code);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-my-campaign-players] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'text-center text-muted py-4';
            msg.textContent = e instanceof Error ? e.message : 'Could not load campaign players. Please try again.';
            container.appendChild(msg);
        }
    })();
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The campaign player table as a TOOL in a host the caller owns — FT3D's Workspace (#div_refresh_gm_viewer) — instead of
 * tools/campaigns/my_campaign_players.php in a new tab (owner 2026-10-14: the GM must not leave the play tab to see the party).
 * Same endpoint, same row renderer and actions; links open beside the game (see openBesideGame). The root declares
 * data-ft3d-workspace-title, so the Workspace bar names the tool and the toolbar carries only search + actions.
 */
export function renderCampaignPlayersTool(host: HTMLElement, campaignId: string): boolean {
    if (!UUID_RE.test(campaignId)) return false;
    const searchId = 'input_search_campaign_players_tool';
    const tool = document.createElement('div');
    tool.className = 'ft-campaign-players-tool p-2';
    tool.dataset.ftCampaignPlayersTool = campaignId;
    tool.dataset.ft3dWorkspaceTitle = 'Campaign Players';
    tool.dataset.ft3dWorkspaceIcon = 'view_campaign_players';

    const toolbar = document.createElement('div');
    toolbar.className = 'ft-cat-toolbar d-flex align-items-stretch gap-2 mb-2 p-2';
    const searchWrap = document.createElement('div');
    searchWrap.className = 'position-relative flex-grow-1 min-w-0 ft-inline-search-root';
    const label = document.createElement('label');
    label.className = 'visually-hidden';
    label.htmlFor = searchId;
    label.textContent = 'Search Campaign Players';
    const search = document.createElement('input');
    search.id = searchId;
    search.type = 'search';
    search.className = 'form-control form-control-sm w-100';
    search.placeholder = 'Search Players';
    search.autocomplete = 'off';
    searchWrap.append(label, search);
    toolbar.appendChild(searchWrap);

    // Filled by mountCatalogMyCampaignPlayers from the endpoint's GM-only invite_code.
    const inviteSlot = document.createElement('div');
    inviteSlot.className = 'd-flex';
    inviteSlot.dataset.ftCampaignInviteSlot = campaignId;
    toolbar.appendChild(inviteSlot);
    const settings = document.createElement('a');
    settings.href = resolveUrl(`campaign_settings.php?campaign_id=${campaignId}`);
    openBesideGame(settings);
    settings.className = 'btn btn-sm btn-info d-flex align-items-center flex-shrink-0';
    settings.setAttribute('aria-label', 'Share Campaign Settings');
    settings.innerHTML = icon('share_campaign_settings', 20, 20, 'Share Campaign Settings');
    toolbar.appendChild(settings);
    tool.appendChild(toolbar);

    const list = document.createElement('div');
    list.className = 'w-100';
    list.dataset.ftCatalogList = '';
    list.dataset.ftRender = 'my-campaign-players';
    list.dataset.ftEndpoint = `options/api/my_campaign_players_list_inc.php?campaign_id=${campaignId}`;
    list.dataset.ftSearchInputId = searchId;
    list.dataset.ftEmptyText = 'This campaign does not have any players yet.';
    list.dataset.ftLinkMode = 'beside-game';
    const results = document.createElement('div');
    results.dataset.ftCatalogResults = '';
    const status = document.createElement('div');
    status.dataset.ftCatalogStatus = '';
    status.className = 'text-center text-muted py-4';
    status.style.display = 'none';
    list.append(results, status);
    tool.appendChild(list);

    // One synchronous swap: the Workspace closes itself when its tool host is left empty, and a clear + fill in the same task is
    // one observer batch, so the window never sees the gap.
    host.replaceChildren(tool);
    mountCatalogList(list);
    return true;
}
